import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from 'semantic-ui-react';
import { connect } from 'react-redux';
import { checkOutWithGateway } from '../../helpers/payments';
import { getCurrencyRate, getPaymentMethods } from '../../api/paymentApi';
import CardLogo from '../../images/card-logo.png';
import { formatMoneyLegacy } from '../../utils/currency';
import './PaymentMethodModal.css';

const KHALTI_LOGO = 'https://khaltibyime.khalti.com/wp-content/uploads/2025/07/Logo-for-Blog.png';
const ESEWA_LOGO = 'https://esewa.com.np/common/images/esewa_logo.png';

const METHOD_META = {
  khalti: { logo: KHALTI_LOGO, logoWidth: '100px' },
  esewa: { logo: ESEWA_LOGO, logoWidth: '90px' },
  card: { logo: CardLogo, logoWidth: '65px' },
};

/**
 * Payment method picker — customer chooses Khalti, eSewa, or international card.
 */
function PaymentMethodModal({
  open,
  onClose,
  currentUser,
  paymentParams,
  amount,
  onPaymentInitiate,
}) {
  const [processing, setProcessing] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [usdToNprRate, setUsdToNprRate] = useState(null);
  const [methods, setMethods] = useState([]);
  const [loadingMethods, setLoadingMethods] = useState(false);

  const listingCurrency = (paymentParams?.currencyCode || 'NPR').toUpperCase();

  useEffect(() => {
    if (!open) return;
    setSelectedMethod(null);
    setLoadingMethods(true);

    const load = async () => {
      try {
        const requests = [getPaymentMethods({ listingCurrency })];
        if (listingCurrency === 'USD') {
          requests.push(getPaymentMethods({ listingCurrency, paymentCurrency: 'NPR' }));
        }
        const responses = await Promise.all(requests);
        const merged = [];
        const seen = new Set();
        responses.forEach((res) => {
          (res.data?.methods || []).forEach((m) => {
            if (!seen.has(m.id)) {
              seen.add(m.id);
              merged.push(m);
            }
          });
        });
        setMethods(merged);
      } catch (e) {
        console.error('Could not load payment methods', e);
        setMethods([
          { id: 'khalti', name: 'Khalti', description: 'Pay with Khalti', available: true },
          { id: 'esewa', name: 'eSewa', description: 'Pay with eSewa', available: true },
        ]);
      } finally {
        setLoadingMethods(false);
      }
    };
    load();
  }, [open, listingCurrency]);

  useEffect(() => {
    if (open && listingCurrency === 'USD') {
      getCurrencyRate()
        .then((res) => setUsdToNprRate(Number(res.data?.usd_to_npr) || 133))
        .catch(() => setUsdToNprRate(133));
    } else {
      setUsdToNprRate(null);
    }
  }, [open, listingCurrency]);

  const rate = usdToNprRate || 133;
  const amountUsd = Number(paymentParams?.amount) || Number(amount) || 0;
  const amountNpr = listingCurrency === 'USD'
    ? Math.round(amountUsd * rate * 100) / 100
    : amountUsd;

  const methodOptions = useMemo(() => methods.map((method) => {
    const meta = METHOD_META[method.id] || METHOD_META.card;
    const paysInNpr = method.id === 'khalti' || method.id === 'esewa';
    const chargeCurrency = paysInNpr ? 'NPR' : listingCurrency;
    const chargeAmount = paysInNpr ? amountNpr : amountUsd;
    let description = method.description || '';
    if (listingCurrency === 'USD' && paysInNpr) {
      description = `${formatMoneyLegacy(chargeAmount, 'NPR')} (1 USD = ${rate} NPR)`;
    } else if (chargeAmount > 0) {
      description = formatMoneyLegacy(chargeAmount, chargeCurrency);
    }
    return {
      ...method,
      ...meta,
      chargeCurrency,
      chargeAmount,
      description,
      recommended: method.id === 'khalti',
    };
  }), [methods, listingCurrency, amountNpr, amountUsd, rate]);

  const handlePay = async (method) => {
    if (processing || !method?.id) return;

    setProcessing(true);
    setSelectedMethod(method.id);
    try {
      const paysInNpr = method.id === 'khalti' || method.id === 'esewa';
      await checkOutWithGateway(method.id, {
        productIdentity: paymentParams.productIdentity,
        amount: paysInNpr ? amountNpr : amountUsd,
        productName: paymentParams.productName || 'Visit All Nepal Booking',
        productUrl: paymentParams.productUrl,
        customerName: paymentParams.customerName,
        customerEmail: paymentParams.customerEmail,
        customerPhone: paymentParams.customerPhone,
        payment_currency: paysInNpr ? 'NPR' : listingCurrency,
      });
      if (onPaymentInitiate) onPaymentInitiate(method.id);
      onClose();
    } catch (error) {
      console.error('Payment error:', error);
      setProcessing(false);
      setSelectedMethod(null);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeIcon
      size="small"
      className="payment-method-modal"
    >
      <Modal.Header>
        <div className="payment-modal-header">
          <h3>Choose payment method</h3>
          {amount != null && (
            <div className="payment-amount">
              {listingCurrency === 'USD' ? (
                <>
                  Total: <strong>{formatMoneyLegacy(amountUsd, 'USD')}</strong>
                  <span className="d-block text-muted small mt-1">
                    Pay in NPR via Khalti/eSewa at {formatMoneyLegacy(amountNpr, 'NPR')}, or pay in USD with card.
                  </span>
                </>
              ) : (
                <>Amount: <strong>{formatMoneyLegacy(amountUsd, listingCurrency)}</strong></>
              )}
            </div>
          )}
        </div>
      </Modal.Header>

      <Modal.Content>
        {loadingMethods ? (
          <p className="text-muted text-center py-4">Loading payment options…</p>
        ) : methodOptions.length === 0 ? (
          <p className="text-muted text-center py-4">No payment methods are available right now. Please contact support.</p>
        ) : (
          <div className="payment-methods-grid">
            {methodOptions.map((method) => (
              <div
                key={method.id}
                role="button"
                tabIndex={0}
                className={`payment-method-card ${
                  selectedMethod === method.id && processing ? 'processing' : ''
                } ${method.recommended ? 'recommended' : ''}`}
                onClick={() => handlePay(method)}
                onKeyDown={(e) => { if (e.key === 'Enter') handlePay(method); }}
              >
                {method.recommended && (
                  <div className="recommended-badge">Popular</div>
                )}
                <div className="payment-method-logo">
                  <img
                    src={method.logo}
                    alt={method.name}
                    style={{ width: method.logoWidth, height: 'auto' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
                <div className="payment-method-name">{method.name}</div>
                <div className="payment-method-description">{method.description}</div>
                {selectedMethod === method.id && processing && (
                  <div className="payment-processing">
                    <div className="spinner" />
                    <span>Redirecting…</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="payment-security-note">
          <i className="fas fa-lock" />
          <span>Select a method above — you will be redirected to complete payment securely.</span>
        </div>
      </Modal.Content>
    </Modal>
  );
}

const mapStateToProps = (state) => ({
  currentUser: state.userStore?.currentUser || {},
});

export default connect(mapStateToProps)(PaymentMethodModal);
